import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { api, ApiError, type Me } from "./api";

/**
 * Loads /api/me. Consumed through MeProvider — call useMeContext(), not this.
 *
 * Signing out is reserved for `invalid_token`: a token the server verified as bad,
 * which genuinely means the session is over. Everything else surfaces as an error
 * the user can read and retry.
 *
 * The earlier version signed out on ANY 401, which turned a server-side
 * misconfiguration into an endless bounce back to the sign-in screen with no
 * message — the failure looked like "login is broken" instead of naming its cause.
 */
export function useMe() {
  const { getToken, isLoaded, isSignedIn, signOut } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Clerk's `getToken` and `signOut` are NOT stable identities — they are new
   * closures on every render. Depending on them makes `load` new every render, which
   * makes the effect below fire every render, and because `load` sets state, that is
   * a fetch loop that never settles: state -> render -> new closure -> effect -> fetch.
   *
   * The same trap was found and fixed in Play.tsx, where it was a POST per render.
   * Same remedy here: the functions live in a ref, and the effect depends only on
   * booleans that actually change when something has happened.
   */
  const authRef = useRef({ getToken, signOut });
  authRef.current = { getToken, signOut };

  const load = useCallback(async () => {
    if (!isSignedIn) {
      setMe(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await api.me(authRef.current.getToken));
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401 && e.code === "invalid_token") {
        await authRef.current.signOut();
        return;
      }
      /**
       * The account was deleted. Clerk still knows this person perfectly well — the
       * deletion never touched their identity — so nothing signs them out on its own,
       * and every screen would sit there retrying an account that is gone. Sign out,
       * which is the only state that is true.
       */
      if (e instanceof ApiError && e.status === 410 && e.code === "account_deleted") {
        await authRef.current.signOut();
        return;
      }
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (isLoaded) void load();
  }, [isLoaded, load]);

  return { me, error, loading, refresh: load };
}
